import { useEffect, useRef, useState } from "react";
import { PAYPAL_CLIENT_ID, PAYPAL_HOSTED_BUTTON_ID } from "@/constants/config";
import { logError } from "@/lib/log";

const SDK_ID = "paypal-hosted-sdk";
let sdkLoadingPromise = null;

function loadPayPalSdk() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.paypal?.HostedButtons) return Promise.resolve(window.paypal);
  if (sdkLoadingPromise) return sdkLoadingPromise;

  sdkLoadingPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SDK_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.paypal));
      existing.addEventListener("error", () => reject(new Error("PayPal SDK failed to load")));
      return;
    }
    const script = document.createElement("script");
    script.id = SDK_ID;
    script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(
      PAYPAL_CLIENT_ID
    )}&components=hosted-buttons&enable-funding=venmo&currency=USD`;
    script.async = true;
    script.onload = () => resolve(window.paypal);
    script.onerror = () => {
      sdkLoadingPromise = null;
      reject(new Error("PayPal SDK failed to load"));
    };
    document.body.appendChild(script);
  });
  return sdkLoadingPromise;
}

/**
 * Renders PayPal's hosted donation button inline.
 * Falls back to a plain link if the SDK can't load (blocked by adblocker, offline, etc.).
 */
export default function PayPalHostedButton({ testId = "paypal-hosted-button" }) {
  const containerRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadPayPalSdk()
      .then((paypal) => {
        if (cancelled || !containerRef.current) return;
        try {
          paypal
            .HostedButtons({ hostedButtonId: PAYPAL_HOSTED_BUTTON_ID })
            .render(containerRef.current);
          setReady(true);
        } catch (err) {
          logError("[PayPalHostedButton] render failed:", err);
          setFailed(true);
        }
      })
      .catch((err) => {
        logError("[PayPalHostedButton] SDK load failed:", err);
        setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (failed) {
    return (
      <a
        href={`https://www.paypal.com/ncp/payment/${PAYPAL_HOSTED_BUTTON_ID}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium"
        style={{ backgroundColor: "#FFFFFF", color: "#0E5C6E" }}
        data-testid={`${testId}-fallback`}
      >
        Donate via PayPal
      </a>
    );
  }

  return (
    <div className="w-full max-w-xs mx-auto" data-testid={testId}>
      {!ready && (
        <div className="h-11 rounded-full bg-white/20 animate-pulse" aria-label="Loading PayPal button" />
      )}
      <div ref={containerRef} id={`paypal-container-${PAYPAL_HOSTED_BUTTON_ID}`} />
    </div>
  );
}
